import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { ADMIN_PATHS, adminPath, adminCompanyPath } from '../../constants/adminPaths';
import { listCustomPlans } from '../services/customPlanApi';
import { PageSkeleton } from '../../../components/loading/skeletons';
import '../components/custom-plan-form.css';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'draft', label: 'Drafts' },
  { id: 'invited', label: 'Invited' },
  { id: 'accepted', label: 'Accepted' },
  { id: 'expired', label: 'Expired' },
];

function formatPrice(plan) {
  const price = plan?.terms?.monthlyPrice;
  if (price == null) return '—';
  const cycle = plan?.terms?.billingCycle === 'annually' ? '/yr (base/mo)' : '/mo';
  return `$${Number(price).toFixed(2)}${cycle === '/mo' ? cycle : ` ${plan.terms.billingCycle}`}`;
}

export default function CustomPlansListPage() {
  const navigate = useNavigate();
  const { admin } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async (status) => {
    try {
      setLoading(true);
      setError('');
      const items = await listCustomPlans({ admin, status });
      setPlans(items);
    } catch (err) {
      setError(err?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (admin?.uid) {
      load(activeTab);
    }
  }, [admin?.uid, activeTab]);

  return (
    <div className="admin-page">
      <div className="admin-page__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h1 className="admin-page__title" style={{ fontSize: '22px', fontWeight: 750, letterSpacing: '-0.02em' }}>
            Custom plans
          </h1>
          <p className="admin-page__subtitle" style={{ margin: '6px 0 0', fontSize: '13px' }}>
            Define a negotiated plan per customer, then send a private invitation link.
          </p>
        </div>
        <button
          type="button"
          className="admin-btn admin-btn--primary"
          onClick={() => navigate(adminPath(`${ADMIN_PATHS.CUSTOM_PLANS}/new`))}
        >
          New custom plan
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`admin-btn${activeTab === tab.id ? ' admin-btn--primary' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && <div className="admin-alert admin-alert--error">{error}</div>}

      {loading ? (
        <PageSkeleton showHeader={false} variant="table" rows={6} label="Loading custom plans" />
      ) : plans.length === 0 ? (
        <div className="cp-detail-card">
          <p style={{ margin: 0 }}>No custom plans yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="admin-card" style={{ overflowX: 'auto' }}>
          <table className="cp-plans-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Plan</th>
                <th>Price</th>
                <th>Email</th>
                <th>Status</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id}>
                  <td>{plan.customerLabel || '—'}</td>
                  <td>{plan.terms?.planName || 'Custom'}</td>
                  <td>{formatPrice(plan)}</td>
                  <td>{plan.invite?.email || '—'}</td>
                  <td>
                    <span className={`cp-status cp-status--${plan.status}`}>{plan.status}</span>
                  </td>
                  <td>{plan.createdAt ? new Date(plan.createdAt).toLocaleDateString() : '—'}</td>
                  <td>
                    <Link to={adminPath(`${ADMIN_PATHS.CUSTOM_PLANS}/${plan.id}`)}>View</Link>
                    {plan.acceptedCompanyId && (
                      <>
                        {' · '}
                        <Link to={adminCompanyPath(plan.acceptedCompanyId)}>Company</Link>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
